import { useState } from "react";
import { auth, db } from "./firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { inputStyle, btnStyle, linkStyle, cardStyle, labelStyle, colors } from "./lib/theme";
import logo from "./assets/logo.png";

export default function AuthPage({ onLogin }) {
  var [mode, setMode] = useState("login"); // login | register | reset
  var [email, setEmail] = useState("");
  var [password, setPassword] = useState("");
  var [displayName, setDisplayName] = useState("");
  var [phone, setPhone] = useState("");
  var [error, setError] = useState("");
  var [info, setInfo] = useState("");
  var [loading, setLoading] = useState(false);

  function clearMessages() { setError(""); setInfo(""); }

  function handleLogin(e) {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    signInWithEmailAndPassword(auth, email, password)
      .then(function (cred) {
        return getDoc(doc(db, "users", cred.user.uid)).then(function (snap) {
          var data = snap.exists() ? snap.data() : {};
          onLogin({ uid: cred.user.uid, email: cred.user.email, displayName: data.displayName || cred.user.email, isAdmin: !!data.isAdmin });
        });
      })
      .catch(function (err) { setError(friendlyError(err.code)); })
      .finally(function () { setLoading(false); });
  }

  function handleRegister(e) {
    e.preventDefault();
    clearMessages();
    if (!displayName.trim()) { setError("Please enter your name."); return; }
    if (!email || !password) { setError("Please enter your email and password."); return; }
    // Checked before the account is created so a typo doesn't leave a half-made account behind.
    if (phone.replace(/\D/g, "").length < 10) { setError("Please enter a valid phone number."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    createUserWithEmailAndPassword(auth, email, password)
      .then(function (cred) {
        return setDoc(doc(db, "users", cred.user.uid), {
          email: cred.user.email,
          displayName: displayName.trim(),
          phone: phone.trim(),
          isAdmin: false,
          createdAt: new Date().toISOString(),
        }).then(function () {
          onLogin({ uid: cred.user.uid, email: cred.user.email, displayName: displayName.trim(), isAdmin: false });
        });
      })
      .catch(function (err) { setError(friendlyError(err.code)); })
      .finally(function () { setLoading(false); });
  }

  function handleReset(e) {
    e.preventDefault();
    clearMessages();
    if (!email) { setError("Enter your email address first."); return; }
    setLoading(true);
    sendPasswordResetEmail(auth, email)
      .then(function () {
        setInfo("Password reset email sent — check your inbox.");
        setMode("login");
      })
      .catch(function (err) { setError(friendlyError(err.code)); })
      .finally(function () { setLoading(false); });
  }

  function friendlyError(code) {
    var msgs = {
      "auth/email-already-in-use": "That email is already registered. Try logging in instead.",
      "auth/invalid-email": "Please enter a valid email address.",
      "auth/wrong-password": "Incorrect password. Try again or reset your password.",
      "auth/user-not-found": "No account found with that email.",
      "auth/weak-password": "Password must be at least 6 characters.",
      "auth/invalid-credential": "Incorrect email or password.",
      "auth/too-many-requests": "Too many attempts. Please wait a moment and try again.",
      "auth/operation-not-allowed": "Email/Password sign-in isn't enabled for this Firebase project yet (Authentication > Sign-in method > Email/Password).",
      "auth/configuration-not-found": "Authentication isn't set up for this Firebase project yet (Authentication > Get started > enable Email/Password).",
      "permission-denied": "Firestore rejected the write — publish firestore.rules in the Firebase console (Firestore Database > Rules).",
      "unavailable": "Couldn't reach Firestore — make sure the Firestore database has been created (Firestore Database > Create database).",
    };
    return msgs[code] || ("Something went wrong (" + code + "). Please try again.");
  }

  return (
    <div style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px", fontFamily: "inherit", color: colors.text }}>
      <div style={{ ...cardStyle, maxWidth: 420, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img src={logo} alt="East Anglia Pickleball" style={{ maxWidth: 220, width: "100%", marginBottom: 12 }} />
          <p style={{ margin: "6px 0 0", fontSize: 13, color: colors.textDim }}>
            {mode === "login" ? "Log in to vote and play" : mode === "register" ? "Create your account" : "Reset your password"}
          </p>
        </div>

        {error && <div style={{ background: colors.dangerBg, border: "1px solid " + colors.dangerBorder, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: colors.danger, marginBottom: 16 }}>{error}</div>}
        {info && <div style={{ background: colors.successBg, border: "1px solid " + colors.successBorder, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: colors.success, marginBottom: 16 }}>{info}</div>}

        {mode === "login" && (
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Email</div>
              <input style={inputStyle} type="email" value={email} onChange={function (e) { setEmail(e.target.value); }} placeholder="your@email.com" required />
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={labelStyle}>Password</div>
              <input style={inputStyle} type="password" value={password} onChange={function (e) { setPassword(e.target.value); }} placeholder="••••••••" required />
            </div>
            <button type="submit" style={btnStyle} disabled={loading}>{loading ? "Logging in..." : "Log In"}</button>
            <div style={{ textAlign: "center", marginTop: 16, display: "flex", justifyContent: "space-between" }}>
              <button type="button" style={linkStyle} onClick={function () { setMode("reset"); clearMessages(); }}>Forgot password?</button>
              <button type="button" style={linkStyle} onClick={function () { setMode("register"); clearMessages(); }}>Create account</button>
            </div>
          </form>
        )}

        {mode === "register" && (
          <form onSubmit={handleRegister}>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Your Name</div>
              <input style={inputStyle} value={displayName} onChange={function (e) { setDisplayName(e.target.value); }} placeholder="Andy Moore" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Email</div>
              <input style={inputStyle} type="email" value={email} onChange={function (e) { setEmail(e.target.value); }} placeholder="your@email.com" required />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>Phone Number</div>
              <input style={inputStyle} type="tel" value={phone} onChange={function (e) { setPhone(e.target.value); }} placeholder="07123 456789" required />
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={labelStyle}>Password</div>
              <input style={inputStyle} type="password" value={password} onChange={function (e) { setPassword(e.target.value); }} placeholder="min. 6 characters" required />
            </div>
            <button type="submit" style={btnStyle} disabled={loading}>{loading ? "Creating account..." : "Create Account"}</button>
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <button type="button" style={linkStyle} onClick={function () { setMode("login"); clearMessages(); }}>Already have an account?</button>
            </div>
          </form>
        )}

        {mode === "reset" && (
          <form onSubmit={handleReset}>
            <div style={{ marginBottom: 20 }}>
              <div style={labelStyle}>Email</div>
              <input style={inputStyle} type="email" value={email} onChange={function (e) { setEmail(e.target.value); }} placeholder="your@email.com" required />
            </div>
            <button type="submit" style={btnStyle} disabled={loading}>{loading ? "Sending..." : "Send Reset Email"}</button>
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <button type="button" style={linkStyle} onClick={function () { setMode("login"); clearMessages(); }}>Back to login</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
